import { Head, Link } from '@inertiajs/react'
import { useState } from 'react'
import { CalendarDays } from 'lucide-react'

import { AppShell } from '@/layouts/AppShell'
import { PageHeader } from '@/components/layout/PageHeader'
import { EmptyState } from '@/components/ui/EmptyState'
import { Badge } from '@/components/ui/Badge'
import { cn } from '@/lib/cn'
import type { Breadcrumb } from '@/types/shared'

type MatchSummary = {
  id: string
  scheduledAt: string
  location: string | null
  isHome: boolean
  opponentName: string | null
  opponentType: 'Team' | 'School'
}

type Props = {
  upcoming: MatchSummary[]
  past: MatchSummary[]
  breadcrumbs: Breadcrumb[]
}

type Tab = 'upcoming' | 'past'

export default function TeamPracticeMatchesIndex({ upcoming, past, breadcrumbs }: Props) {
  const [tab, setTab] = useState<Tab>('upcoming')
  const matches = tab === 'upcoming' ? upcoming : past

  return (
    <AppShell>
      <Head title="練習試合" />
      <PageHeader
        title="練習試合"
        description="自チームの練習試合の日程を確認できます。日程の登録は管理者が行います。"
        breadcrumbs={breadcrumbs}
      />

      <div className="mb-4 flex gap-2">
        <TabButton label="今後の予定" count={upcoming.length} active={tab === 'upcoming'} onClick={() => setTab('upcoming')} />
        <TabButton label="過去の試合" count={past.length} active={tab === 'past'} onClick={() => setTab('past')} />
      </div>

      {matches.length === 0 ? (
        <EmptyState
          icon={<CalendarDays className="h-8 w-8" />}
          title={tab === 'upcoming' ? '今後の練習試合はありません' : '過去の練習試合はありません'}
          description="管理者が日程を登録すると、こちらに表示されます。"
        />
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {matches.map((match) => (
            <Link
              key={match.id}
              href={`/team/practice_matches/${match.id}`}
              className="rounded-2xl border border-white/10 bg-white/5 p-5 transition hover:border-white/20 hover:bg-white/10"
            >
              <div className="flex items-center justify-between gap-2">
                <p className="text-base font-semibold text-white">vs {match.opponentName ?? '?'}</p>
                <Badge variant={match.isHome ? 'success' : 'outline'}>{match.isHome ? 'ホーム' : 'アウェー'}</Badge>
              </div>
              <p className="mt-2 text-sm text-white/70">{formatDateTime(match.scheduledAt)}</p>
              {match.location ? (
                <p className="mt-1 text-xs text-white/50">会場: {match.location}</p>
              ) : null}
            </Link>
          ))}
        </div>
      )}
    </AppShell>
  )
}

function TabButton({
  label,
  count,
  active,
  onClick,
}: {
  label: string
  count: number
  active: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded-full px-4 py-1.5 text-sm transition border',
        active
          ? 'bg-brand-500 border-brand-500 text-white'
          : 'bg-white/5 border-white/10 text-white/70 hover:bg-white/10',
      )}
    >
      {label}
      <span className="ml-2 text-xs opacity-70">{count}</span>
    </button>
  )
}

function formatDateTime(value: string) {
  return new Date(value).toLocaleString('ja-JP', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    weekday: 'short',
  })
}
